import { createContext, useContext, useEffect, useState } from 'react'
import { post, get, obtainToken, bindUserSession } from '../api/auth'
import { useLocalStorage } from '../hooks/useLocalStorage'
import { message } from 'antd'
import { useCustomisation } from './CustomisationProvider'

const AuthContext = createContext({})

export const useAuth = () => {
  return useContext(AuthContext)
}

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null)
  const [emailConfirmationLinks, setEmailConfirmationLinks] = useState(null)
  const [user, setUser] = useLocalStorage('user', '')
  const [userId, setUserId] = useLocalStorage('userId', '')
  const { apiHost, realm } = useCustomisation()

  useEffect(() => {
    const authenticate = async () => {
      const currentToken = await obtainToken()
      if (currentToken) {
        setToken(currentToken.auth_token)
      }
    }
    authenticate()
  }, [])

  useEffect(() => {
    if (token && apiHost) {
      getBusinessPartner(token)
    }
  }, [token, apiHost])

  const getBusinessPartner = async (myToken) => {
    try {
      const result = await get(`https://${apiHost}/resource/v3/me`, myToken)
      if (result['business_partner']) {
        setUser(result['business_partner']['screenname'])
        setUserId(result['business_partner']['_id'])
      }
    } catch (error) {
      message.error(`Authentication failed:\n${error}`)
    }
  }

  const verifyEmail = async (url, onSuccess, onError) => {
    try {
      const result = await get(url, token)
      if (emailVerificationNeeded(result['status'])) {
        setEmailConfirmationLinks(result['_links'])
        onError(result['status'])
      } else if (result['error']) {
        onError(result['error'])
      } else {
        if (result.auth_token) {
          // Attempt to bind user session
          const bindResult = await bindUserSession(result.auth_token)
          if (!bindResult) {
            onError('Failed to login. Please try again.')
            return false
          } else {
            setToken(result.auth_token)
          }
        }
        onSuccess(result)
      }
    } catch (error) {
      onError(error)
    }
    return true
  }

  const handleAuthRequest = async (url, values, onSuccess, onError) => {
    const newValues = { ...values, realm: realm }
    const result = await post(url, newValues)
    if (emailVerificationNeeded(result['status'])) {
      setEmailConfirmationLinks(result['_links'])
      onError(result['status'])
    } else if (result['error']) {
      onError(result['error'])
    } else {
      if (result.auth_token) {
        // Attempt to bind user session
        const bindResult = await bindUserSession(result.auth_token)
        if (!bindResult) {
          onError('Failed to login. Please try again.')
          return false
        } else {
          setToken(result.auth_token)
        }
      }
      onSuccess()
    }
    return true
  }

  const handleLogin = (values, onSuccess, onError) => {
    return handleAuthRequest(
      `https://${apiHost}/auth/auth`,
      values,
      onSuccess,
      onError
    )
  }
  const handleRegister = (values, onSuccess, onError) => {
    return handleAuthRequest(
      `https://${apiHost}/auth/register`,
      values,
      onSuccess,
      onError
    )
  }
  const handleRequestNewPassword = (values, onSuccess, onError) => {
    return handleAuthRequest(
      `https://${apiHost}/auth/recover`,
      values,
      onSuccess,
      onError
    )
  }

  const handleRequestConfirmEmail = (values, onSuccess, onError) => {
    verifyEmail(
      emailConfirmationLinks.verify_code_url + values.code,
      onSuccess,
      onError
    )
  }

  const handleRequestResendCode = (values, onSuccess, onError) => {
    verifyEmail(emailConfirmationLinks.resend_code_url, onSuccess, onError)
  }

  const handleRequestCheckStatus = (onSuccess, onError) => {
    verifyEmail(emailConfirmationLinks.status_url, onSuccess, onError)
  }

  const getUserNames = () => {
    const parts = user.split(' ')
    let firstName = ''
    if (parts[0].endsWith('.')) {
      // like Dr.
      firstName = parts[1]
    } else {
      firstName = parts[0]
    }
    return { firstName, lastName: parts[parts.length - 1] }
  }
  const emailVerificationNeeded = (statusMsg) => {
    return statusMsg === 'email-verification'
  }

  const handleLogout = () => {
    setToken(null)
    setUser(null)
    setUserId(null)
    console.log('logout')
  }
  const value = {
    token,
    apiHost,
    user,
    getUserNames: getUserNames,
    userId,
    onLogin: handleLogin,
    onRegister: handleRegister,
    onRequestNewPassword: handleRequestNewPassword,
    onConfirmEmail: handleRequestConfirmEmail,
    onCheckStatus: handleRequestCheckStatus,
    onResendCode: handleRequestResendCode,
    onLogout: handleLogout,
    emailVerificationNeeded: emailVerificationNeeded,
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
